import { BaseInfoContext } from '@/context/BaseInfoContext';
import { Perms } from '@/models/perms';
import {
  BankOutlined,
  BarcodeOutlined,
  CalendarOutlined,
  CreditCardOutlined,
  DashboardOutlined,
  DatabaseOutlined,
  DeploymentUnitOutlined,
  FileTextOutlined,
  LineChartOutlined,
  NotificationOutlined,
  RadarChartOutlined,
  RobotOutlined,
  SettingOutlined,
  TeamOutlined,
  UserOutlined,
  VideoCameraOutlined,
  WechatOutlined,
  WechatWorkOutlined,
} from '@ant-design/icons';
import { useContext, useMemo } from 'react';

export const useAppMenuItems = () => {
  const [, , { hasPermission }] = useContext(BaseInfoContext);

  const menuItems = useMemo(() => {
    const items = [];

    items.push({
      key: 'dashboard',
      label: (
        <>
          <DashboardOutlined />
          <span>总览</span>
        </>
      ),
    });

    items.push({
      key: 'statistics',
      label: (
        <span>
          <LineChartOutlined />
          <span>数据报表</span>
        </span>
      ),
      children: [
        hasPermission(Perms.STATISTICS_VISIT) ? { key: 'statistics-visit', label: '小程序访问' } : null,
        hasPermission(Perms.STATISTICS_GOODS) ? { key: 'statistics-sku', label: '商品概况' } : null,
        hasPermission(Perms.STATISTICS_OP) ? { key: 'statistics-order', label: '经营概况' } : null,
      ].filter(Boolean),
    });

    items.push({
      key: 'sku',
      label: (
        <span>
          <BarcodeOutlined />
          <span>商品管理</span>
        </span>
      ),
      children: [
        hasPermission(Perms.GOODS_CATEGORY) ? { key: 'sku-category', label: '品类管理' } : null,
        hasPermission(Perms.GOODS_GOODS) ? { key: 'sku-basic', label: '商品信息' } : null,
        hasPermission(Perms.GOODS_GOODS) ? { key: 'sku-related', label: '商品关联关系' } : null,
      ].filter(Boolean),
    });

    if (hasPermission(Perms.GOODS_GOODS)) {
      items.push({
        key: 'inventory',
        label: (
          <span>
            <DatabaseOutlined />
            <span>库存管理</span>
          </span>
        ),
        children: [
          { key: 'inventory-stock', label: '库存列表' },
          { key: 'inventory-purchase', label: '进货' },
          { key: 'inventory-purchaseHistory', label: '进货记录' },
          { key: 'inventory-stockTaking', label: '盘点' },
          { key: 'inventory-stockTakingHistory', label: '盘点历史' },
          { key: 'inventory-supplier', label: '供应商' },
        ],
      });
    }
    if (hasPermission(Perms.ORDER_LIST)) {
      items.push({
        key: 'order',
        label: (
          <>
            <FileTextOutlined />
            <span>订单管理</span>
          </>
        ),
      });
    }

    items.push({
      key: 'appointment',
      label: (
        <>
          <CalendarOutlined />
          <span>预约管理</span>
        </>
      ),
      children: [
        { key: 'appointment-list', label: '预约列表' },
        { key: 'appointment-assistedBook', label: '代客预约' },
        { key: 'appointment-specialSlots', label: '特殊时段管理' },
      ],
    });

    if (hasPermission(Perms.USER_LIST)) {
      items.push({
        key: 'user',
        label: (
          <>
            <UserOutlined />
            <span>客户管理</span>
          </>
        ),
      });
    }

    items.push({
      key: 'mkt',
      label: (
        <>
          <RadarChartOutlined />
          <span>精准营销</span>
        </>
      ),
      children: [{ key: 'mkt-task', label: '任务列表' }],
    });

    items.push({
      key: 'wecom',
      label: (
        <span>
          <WechatWorkOutlined />
          <span>企微</span>
        </span>
      ),
      children: [
        { key: 'wecom-externalContact', label: '客户管理' },
        { key: 'wecom-welcome', label: '欢迎语配置' },
      ],
    });

    items.push({
      key: 'moreme',
      label: (
        <span>
          <RobotOutlined />
          <span>检测仪</span>
        </span>
      ),
      children: [
        { key: 'moreme-user', label: '检测仪客户管理' },
        { key: 'moreme-report', label: '检测仪报告' },
      ],
    });

    items.push({
      key: 'marketing',
      label: (
        <span>
          <NotificationOutlined />
          <span>营销工具</span>
        </span>
      ),
      children: [
        hasPermission(Perms.MARKETING_COUPON) ? { key: 'marketing-coupon', label: '优惠券营销' } : null,
        hasPermission(Perms.MARKETING_COUPON) ? { key: 'marketing-couponUser', label: '用户优惠券管理' } : null,
        hasPermission(Perms.MARKETING_POINT) ? { key: 'marketing-point', label: '积分与储值营销' } : null,
        hasPermission(Perms.MARKETING_PROMOTION) ? { key: 'marketing-secKill', label: '商品促销活动' } : null,
        hasPermission(Perms.MARKETING_CARD) ? { key: 'marketing-card', label: '预付卡' } : null,
        hasPermission(Perms.MARKETING_CARD) ? { key: 'marketing-cardUser', label: '用户预付卡' } : null,
        hasPermission(Perms.MARKETING_RECHARGE) ? { key: 'marketing-recharge', label: '会员充值' } : null,
        { key: 'marketing-campaign', label: '营销活动页' },
      ].filter(Boolean),
    });

    items.push({
      key: 'operation',
      label: (
        <span>
          <CreditCardOutlined />
          <span>运营管理</span>
        </span>
      ),
      children: [
        hasPermission(Perms.OPERATION_NOTICE) ? { key: 'operation-notice', label: '公告栏管理' } : null,
        hasPermission(Perms.OPERATION_AD) ? { key: 'operation-ad', label: '广告位管理' } : null,
      ].filter(Boolean),
    });

    items.push({
      key: 'staff',
      label: (
        <span>
          <TeamOutlined />
          <span>员工管理</span>
        </span>
      ),
      children: [
        hasPermission(Perms.STAFF_LIST) ? { key: 'staff-basic', label: '基础信息' } : null,
        hasPermission(Perms.STAFF_ROLE) ? { key: 'staff-role', label: '角色与权限' } : null,
        hasPermission([Perms.STAFF_ATTENDANCE, Perms.STAFF_KPI])
          ? { key: 'staff-attendance', label: '考勤与绩效' }
          : null,
        { key: 'staff-handoverLog', label: '交接班记录' },
      ].filter(Boolean),
    });

    items.push({
      key: 'poi',
      label: (
        <span>
          <BankOutlined />
          <span>门店管理</span>
        </span>
      ),
      children: [
        hasPermission(Perms.POI_BASIC) ? { key: 'poi-basic', label: '基础信息' } : null,
        hasPermission(Perms.POI_APPOINTMENT) ? { key: 'poi-appointment', label: '预约规则' } : null,
      ].filter(Boolean),
    });

    items.push({
      key: 'weapp',
      label: (
        <span>
          <WechatOutlined />
          <span>小程序</span>
        </span>
      ),
      children: [
        { key: 'weapp-basic', label: '基础信息' },
        { key: 'weapp-code', label: '发布与更新' },
        { key: 'weapp-style', label: '样式设置' },
      ],
    });

    if (hasPermission(Perms.POI_BASIC)) {
      items.push({
        key: 'settings',
        label: (
          <span>
            <SettingOutlined />
            <span>设置</span>
          </span>
        ),
        children: [
          { key: 'settings-mall', label: '商城配置' },
          { key: 'settings-payment', label: '支付配置' },
          { key: 'settings-sms', label: '短信配置' },
          { key: 'settings-notifications', label: '消息与通知' },
          { key: 'settings-protocols', label: '协议管理' },
        ],
      });
    }

    items.push({
      key: 'system',
      label: (
        <span>
          <DeploymentUnitOutlined />
          <span>平台管理</span>
        </span>
      ),
      children: [
        hasPermission(Perms.SYS_TENANT) ? { key: 'system-poi', label: '租户管理' } : null,
        hasPermission(Perms.SYS_ACCOUNT) ? { key: 'system-staff', label: '账号管理' } : null,
        hasPermission(Perms.SYS_POS) ? { key: 'system-device-pos', label: 'POS机管理' } : null,
        hasPermission(Perms.SYS_APP) ? { key: 'system-app-pos', label: '应用管理' } : null,
        hasPermission(Perms.SYS_SP) ? { key: 'system-sp-list', label: '标准化商品库' } : null,
        hasPermission(Perms.SYS_PERMISSION) ? { key: 'system-permission', label: '权限管理' } : null,
      ].filter(Boolean),
    });
    return items.filter(item => !item.children || item.children.length > 0);
  }, [hasPermission]);

  return menuItems;
};
